import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { SearchInput } from '../components/ui/SearchInput';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  Users,
  Search,
  MoreHorizontal,
  Lock,
  Unlock,
  Trash2,
  Crown,
} from 'lucide-react';

interface User {
  id: string;
  username: string;
  email: string;
  nickname?: string;
  avatar?: string;
  role: string;
  status: string;
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  teamCount: number;
  workflowCount: number;
  aiUsageCount: number;
}

export function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await userApi.listUsers({
        page,
        pageSize,
        keyword: keyword || undefined,
        status: status || undefined,
      });
      if (response.code === 0) {
        setUsers(response.data.items);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, keyword, status]);

  const handleFreeze = async (userId: string) => {
    try {
      await userApi.freezeUser(userId);
      fetchUsers();
    } catch (err) {
      console.error('Failed to freeze user:', err);
    }
  };

  const handleUnfreeze = async (userId: string) => {
    try {
      await userApi.unfreezeUser(userId);
      fetchUsers();
    } catch (err) {
      console.error('Failed to unfreeze user:', err);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!confirm('确定要删除此用户吗？此操作不可恢复。')) return;
    try {
      await userApi.deleteUser(userId);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">正常</Badge>;
      case 'inactive':
        return <Badge variant="warning">未激活</Badge>;
      case 'locked':
        return <Badge variant="error">已冻结</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-600">
            <Crown className="w-3 h-3" />
            管理员
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-600">
            <Users className="w-3 h-3" />
            用户
          </span>
        );
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('zh-CN');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">用户管理</h1>
          <p className="text-zinc-500 mt-1">管理平台所有用户账号</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchInput
              placeholder="搜索用户名、邮箱..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onClear={() => setKeyword('')}
              className="sm:w-80"
            />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
            >
              <option value="">所有状态</option>
              <option value="active">正常</option>
              <option value="inactive">未激活</option>
              <option value="locked">已冻结</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>用户列表</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>用户</TableHead>
                    <TableHead>角色</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>团队</TableHead>
                    <TableHead>工作流</TableHead>
                    <TableHead>AI 使用</TableHead>
                    <TableHead>注册时间</TableHead>
                    <TableHead>最后登录</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                            {user.avatar ? (
                              <img
                                src={user.avatar}
                                alt={user.username}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-accent-700 font-semibold">
                                {user.username.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-zinc-900">{user.username}</p>
                            <p className="text-sm text-zinc-500">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell>{getStatusBadge(user.status)}</TableCell>
                      <TableCell>{user.teamCount}</TableCell>
                      <TableCell>{user.workflowCount}</TableCell>
                      <TableCell>{user.aiUsageCount}</TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>
                        {user.lastLoginAt ? formatDate(user.lastLoginAt) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="relative inline-block">
                          <button
                            onClick={() => setShowActionMenu(showActionMenu === user.id ? null : user.id)}
                            className="p-2 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          
                          {showActionMenu === user.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg border border-zinc-200 shadow-lg z-50">
                              <button
                                onClick={() => {
                                  navigate(`/users/${user.id}`);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                              >
                                <Search className="w-4 h-4" />
                                查看详情
                              </button>
                              
                              {user.status === 'active' ? (
                                <button
                                  onClick={() => {
                                    handleFreeze(user.id);
                                    setShowActionMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-warning-600 hover:bg-warning-50"
                                >
                                  <Lock className="w-4 h-4" />
                                  冻结账号
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    handleUnfreeze(user.id);
                                    setShowActionMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-success-600 hover:bg-success-50"
                                >
                                  <Unlock className="w-4 h-4" />
                                  解冻账号
                                </button>
                              )}
                              
                              <button
                                onClick={() => {
                                  handleDelete(user.id);
                                  setShowActionMenu(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-error-600 hover:bg-error-50"
                              >
                                <Trash2 className="w-4 h-4" />
                                删除账号
                              </button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
